// Shared (client + server) validation for Spotlight UTM parameters.
//
// The value is a URL query fragment appended to a Spotlight's destination link,
// e.g. "utm_source=meta&utm_medium=paid_social&utm_campaign=spotlight". We strip
// a leading ?/& when appending (see appendUtmParams in spotlight_helpers.js), so a
// leading separator is tolerated here too. Empty/null is valid — it clears a
// per-spotlight override (inherit the brand default) or the brand default itself.

// Matches the varchar(1024) column on SpotlightSettings / Spotlights / requests.
const UTM_PARAMS_MAX_LENGTH = 1024;

// Attribution-reserved keys a brand-writable fragment may never carry: the click redirect owns
// utm_term (it writes the ShopMy click id) and smsclickid. Prefix-matched, so smsclickid variants
// are covered too. Opt-in via validateUtmParams' reservedKeys option — the admin-only per-spotlight
// override keeps accepting these keys.
const RESERVED_UTM_PARAM_KEYS = ['utm_term', 'smsclickid'];

/**
 * @param {string|null|undefined} raw
 * @param {{ reservedKeys?: string[] }} [options] — prefix-matched keys stripped from the fragment
 *   rather than rejected; the stripped fragment comes back as `sanitized`.
 * @returns {{ valid: boolean, error: string|null, sanitized?: string|null }} — `sanitized` only on
 *   valid results: the fragment minus reserved keys and leading separators, or null when empty.
 */
const validateUtmParams = (raw, { reservedKeys = [] } = {}) => {
  if (raw === null || raw === undefined) return { valid: true, error: null, sanitized: null };

  const value = String(raw).trim();
  if (!value) return { valid: true, error: null, sanitized: null };

  if (value.length > UTM_PARAMS_MAX_LENGTH) {
    return { valid: false, error: `Too long — keep UTM parameters under ${UTM_PARAMS_MAX_LENGTH} characters.` };
  }

  // Tolerate a leading ?/& (we strip it when appending), then validate the rest.
  const cleaned = value.replace(/^[?&]+/, '');

  if (/\s/.test(cleaned)) {
    return { valid: false, error: 'Remove spaces — encode them as %20 or + if needed.' };
  }
  if (/[#?]/.test(cleaned)) {
    return { valid: false, error: 'Enter only the query parameters — no ? or # characters.' };
  }

  // utm_* keys match exactly (utm_terms is a legitimate brand key); non-utm entries prefix-match
  // to cover vendor variants like smsclickid2.
  const isReserved = key =>
    reservedKeys.some(reserved => (reserved.startsWith('utm_') ? key.toLowerCase() === reserved : key.toLowerCase().startsWith(reserved)));
  const keptPairs = [];
  const pairs = cleaned.split('&');
  for (const pair of pairs) {
    if (!pair) {
      return { valid: false, error: 'Remove empty parameters (stray "&" separators).' };
    }
    const eqIndex = pair.indexOf('=');
    const key = eqIndex === -1 ? pair : pair.slice(0, eqIndex);
    // Reserved keys drop before the form checks — a bare "utm_term" is stripped, not rejected.
    if (key && isReserved(key)) continue;
    if (eqIndex === -1) {
      return { valid: false, error: `"${pair}" is missing a value — use the form key=value.` };
    }
    if (!key) {
      return { valid: false, error: 'Each parameter needs a name before the "=" sign.' };
    }
    if (!/^[A-Za-z0-9_.-]+$/.test(key)) {
      return { valid: false, error: `"${key}" has invalid characters in the parameter name.` };
    }
    keptPairs.push(pair);
  }

  return { valid: true, error: null, sanitized: keptPairs.join('&') || null };
};

/**
 * Normalize a UTM query fragment to a bare query string with no leading/trailing
 * ?/& separators (e.g. "?utm_source=meta&" → "utm_source=meta"). Returns null when
 * the result is empty. This is the form Meta expects for an AdCreative's `url_tags`
 * field and the form appendUtmParams joins onto a URL.
 *
 * @param {string|null|undefined} utm
 * @returns {string|null}
 */
const normalizeUtmParams = utm => {
  if (!utm) return null;
  const cleaned = String(utm)
    .trim()
    .replace(/^[?&]+/, '')
    .replace(/[?&]+$/, '');
  return cleaned || null;
};

/**
 * Append a UTM query fragment to a destination URL. Strips a leading ?/& off the
 * fragment and joins with the correct separator depending on whether the URL
 * already has a query string. Returns the url unchanged when utm is empty.
 *
 * Pure and isomorphic — used client-side for link previews (SpotlightDetailsModal).
 * The Meta submission path no longer appends UTMs to the URL; it passes them as the
 * creative's `url_tags` field instead (see normalizeUtmParams in spotlight_helpers.js).
 *
 * @param {string|null|undefined} url
 * @param {string|null|undefined} utm
 * @returns {string|null|undefined}
 */
const appendUtmParams = (url, utm) => {
  if (!url || !utm) return url;
  const cleaned = normalizeUtmParams(utm);
  if (!cleaned) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}${cleaned}`;
};

// Named UTM presets (SpotlightSettings.utmPresets): a JSON array of { name, utmParams } a brand
// builds once and applies per set. Applying one snapshots its utmParams onto the set's own column,
// so editing a preset never touches sets that already used it.
const UTM_PRESETS_MAX = 20;
const UTM_PRESET_NAME_MAX_LENGTH = 60;

/**
 * @param {Array<{ name: string, utmParams: string }>|null|undefined} presets
 * @returns {{ valid: boolean, error: string|null }}
 */
const validateUtmPresets = presets => {
  if (presets === null || presets === undefined) return { valid: true, error: null };
  if (!Array.isArray(presets)) return { valid: false, error: 'Presets must be a list.' };
  if (presets.length > UTM_PRESETS_MAX) {
    return { valid: false, error: `You can save up to ${UTM_PRESETS_MAX} presets — remove one before adding another.` };
  }
  const seenNames = new Set();
  for (const preset of presets) {
    if (!preset || typeof preset !== 'object' || Array.isArray(preset)) {
      return { valid: false, error: 'Each preset needs a name and UTM parameters.' };
    }
    const name = typeof preset.name === 'string' ? preset.name.trim() : '';
    if (!name) return { valid: false, error: 'Each preset needs a name.' };
    if (name.length > UTM_PRESET_NAME_MAX_LENGTH) {
      return { valid: false, error: `Keep preset names under ${UTM_PRESET_NAME_MAX_LENGTH} characters.` };
    }
    const nameKey = name.toLowerCase();
    if (seenNames.has(nameKey)) return { valid: false, error: `You already have a preset named "${name}".` };
    seenNames.add(nameKey);
    const utmValidation = validateUtmParams(preset.utmParams);
    if (!utmValidation.valid) return { valid: false, error: `Preset "${name}": ${utmValidation.error}` };
    if (!normalizeUtmParams(preset.utmParams)) return { valid: false, error: `Preset "${name}" has no UTM parameters.` };
  }
  return { valid: true, error: null };
};

// Validation for the free-text reason a creator gives when pausing a Spotlight.
// The textarea caps input at 250 chars (PAUSE_REASON_MAX_LENGTH); here we enforce
// a meaningful minimum so low-quality placeholders like "XX" can't be submitted.
// A reason IS required — the brand sees this text on the paused card.
const PAUSE_REASON_MIN_LENGTH = 10;
const PAUSE_REASON_MAX_LENGTH = 250;

/**
 * @param {string|null|undefined} raw
 * @returns {{ valid: boolean, error: string|null }}
 */
const validatePauseReason = raw => {
  const value = String(raw === null || raw === undefined ? '' : raw).trim();

  if (!value) {
    return { valid: false, error: 'Please explain why you are pausing this Spotlight.' };
  }
  if (value.length < PAUSE_REASON_MIN_LENGTH) {
    return { valid: false, error: `Please provide a bit more detail (at least ${PAUSE_REASON_MIN_LENGTH} characters).` };
  }
  if (value.length > PAUSE_REASON_MAX_LENGTH) {
    return { valid: false, error: `Keep your reason under ${PAUSE_REASON_MAX_LENGTH} characters.` };
  }

  return { valid: true, error: null };
};

// Minimum daily budget (dollars) a brand may set for Spotlights. Enforced on every
// brand-facing surface — the onboarding budget step and the Manage Budget modal — on
// both client and server. Relaxed per brand when an admin sets SpotlightSettings.allowBelowMinDailyBudget.
const SPOTLIGHT_MIN_DAILY_BUDGET = 150;

/**
 * @param {number|string|null|undefined} raw
 * @param {{ allowBelowMin?: boolean }} [options]
 * @returns {{ valid: boolean, error: string|null }}
 */
const validateDailyBudget = (raw, { allowBelowMin = false, allowZero = false } = {}) => {
  const value = typeof raw === 'number' ? raw : parseFloat(raw);

  // $0 is the confirmed "pause all Spotlights" action, never a budget: only the off flow passes
  // allowZero, so every other writer keeps rejecting it.
  if (allowZero && value === 0) {
    return { valid: true, error: null };
  }
  if (isNaN(value) || value <= 0) {
    return { valid: false, error: 'Please enter a valid budget amount.' };
  }
  if (!allowBelowMin && value < SPOTLIGHT_MIN_DAILY_BUDGET) {
    return { valid: false, error: `Daily budget must be at least $${SPOTLIGHT_MIN_DAILY_BUDGET}.` };
  }
  return { valid: true, error: null };
};

// Bounds on a target ROAS, whether the brand's or a strategy's override. The floor equals the pause
// guide's floor so "pause guide >= floor AND <= target" always has a solution.
const SPOTLIGHT_TARGET_ROAS_FLOOR = 0.3;
const SPOTLIGHT_TARGET_ROAS_CEILING = 10;
// The band a brand grades against until it sets its own.
const SPOTLIGHT_DEFAULT_TARGET_ROAS = 1.3;
const SPOTLIGHT_DEFAULT_PAUSE_GUIDE_ROAS = 0.3;
const SPOTLIGHT_PAUSE_GUIDE_FLOOR = SPOTLIGHT_TARGET_ROAS_FLOOR;

// Clamp a brand's raw target/pause-guide inputs into a valid band.
const resolveTargetRoasBand = ({ targetRoas, pauseGuideRoas } = {}) => {
  const target = Math.min(
    SPOTLIGHT_TARGET_ROAS_CEILING,
    Math.max(SPOTLIGHT_TARGET_ROAS_FLOOR, Number(targetRoas) > 0 ? Number(targetRoas) : SPOTLIGHT_DEFAULT_TARGET_ROAS)
  );
  const rawPause = Number(pauseGuideRoas) > 0 ? Number(pauseGuideRoas) : SPOTLIGHT_DEFAULT_PAUSE_GUIDE_ROAS;
  const pauseLine = Math.min(target, Math.max(SPOTLIGHT_PAUSE_GUIDE_FLOOR, rawPause));
  return { target, pauseLine };
};

// A spotlight's band: the brand's, unless its primary strategy overrides the target. An override
// keeps the brand's pause-to-target ratio, so a stricter target also pauses sooner. Callers pass a
// null override for a primary strategy that isn't active.
const resolveSpotlightTargetBand = (brandSettings, groupTargetRoas) => {
  const brandBand = resolveTargetRoasBand(brandSettings || {});
  if (groupTargetRoas == null) return brandBand;
  return resolveTargetRoasBand({ targetRoas: groupTargetRoas, pauseGuideRoas: Number(groupTargetRoas) * (brandBand.pauseLine / brandBand.target) });
};

/**
 * @param {number|string|null|undefined} raw
 * @returns {{ valid: boolean, error: string|null }}
 */
const validateTargetRoas = raw => {
  const value = typeof raw === 'number' ? raw : typeof raw === 'string' && raw.trim() ? Number(raw) : NaN;

  if (!Number.isFinite(value)) {
    return { valid: false, error: 'Please enter a valid target ROAS.' };
  }
  if (value < SPOTLIGHT_TARGET_ROAS_FLOOR || value > SPOTLIGHT_TARGET_ROAS_CEILING) {
    return { valid: false, error: `Target ROAS must be between ${SPOTLIGHT_TARGET_ROAS_FLOOR}x and ${SPOTLIGHT_TARGET_ROAS_CEILING}x.` };
  }
  return { valid: true, error: null };
};

/**
 * A pause limit above the target is accepted here; readers clamp it to the target.
 * @param {number|string|null|undefined} raw
 * @returns {{ valid: boolean, error: string|null }}
 */
const validatePauseGuideRoas = raw => {
  const value = typeof raw === 'number' ? raw : typeof raw === 'string' && raw.trim() ? Number(raw) : NaN;

  if (!Number.isFinite(value)) {
    return { valid: false, error: 'Please enter a valid pause limit.' };
  }
  if (value < SPOTLIGHT_PAUSE_GUIDE_FLOOR || value > SPOTLIGHT_TARGET_ROAS_CEILING) {
    return { valid: false, error: `Pause limit must be between ${SPOTLIGHT_PAUSE_GUIDE_FLOOR}x and ${SPOTLIGHT_TARGET_ROAS_CEILING}x.` };
  }
  return { valid: true, error: null };
};

// Brand-facing, the feature reads as "Spotlight Strategies"; every internal identifier (table,
// route, column, redux key, component name, css class) stays SpotlightGroup. Route user-visible
// copy through these so the noun has one home and never drifts. The plural is spelled out rather
// than derived — appending an "s" only works for nouns that pluralize regularly.
const TERM = 'Strategy';
const TERM_PLURAL = 'Strategies';

// The Meta placements a Spotlight ad set may target. Partnership Ads cannot run in Search Results,
// Marketing Messages or Audience Network, so those keys exist nowhere: the client option lists
// derive from this, the routes refuse anything else, the targeting builders drop it, and the
// maintenance sweep's reverse map never emits it.
const SPOTLIGHT_META_PLACEMENTS = [
  { value: 'feeds', label: 'Feeds (1:1 or 4:5)' },
  { value: 'stories_reels', label: 'Stories, Status, Reels (9:16)' },
  { value: 'in_stream_reels', label: 'In-Stream Ads for Reels (16:9, 9:16, or 1:1)' }
];
const SPOTLIGHT_META_PLACEMENT_VALUES = SPOTLIGHT_META_PLACEMENTS.map(option => option.value);

/**
 * The allowlisted keys of a stored placements list — the JSON-text column or its parsed array —
 * deduped, in input order. Anything that is not a list yields an empty list.
 * @param {unknown} placements
 * @returns {string[]}
 */
const allowlistedMetaPlacements = placements => {
  const list = parseStoredPlacements(placements);
  return Array.isArray(list) ? list.filter((value, index) => SPOTLIGHT_META_PLACEMENT_VALUES.includes(value) && list.indexOf(value) === index) : [];
};

// The stored column as written (JSON text) or as already parsed; anything unreadable is no list.
const parseStoredPlacements = placements => {
  if (typeof placements !== 'string') return placements;
  try {
    return JSON.parse(placements);
  } catch {
    return null;
  }
};

/**
 * Whether a stored list carries every allowlisted placement — the "Default placements" reading.
 * Legacy rows that also carry a retired key still count as all of them.
 * @param {unknown} placements
 * @returns {boolean}
 */
const includesEveryMetaPlacement = placements => allowlistedMetaPlacements(placements).length === SPOTLIGHT_META_PLACEMENT_VALUES.length;

// A stored list read through the allowlist. One the allowlist emptied becomes every placement — the
// brand had narrowed to keys that no longer exist, and [] would run Meta's defaults instead. An
// already-empty list stays empty; anything that is not an array yields an empty list.
const normalizedMetaPlacements = placements => {
  const list = parseStoredPlacements(placements);
  if (!Array.isArray(list)) return [];
  const kept = allowlistedMetaPlacements(list);
  return kept.length || !list.length ? kept : [...SPOTLIGHT_META_PLACEMENT_VALUES];
};

// Hidden non-default groups are admin plumbing; the Default group is exempt so the brand UI always
// keeps its anchor. visibleInUi is a tinyint; a row without it reads hidden, so a select list that
// forgets the column drops admin-hidden sets rather than leaking them.
const isBrandVisibleGroup = group => !!group?.isDefault || !!Number(group?.visibleInUi);

// Statuses in which spend is (or could be) running. 'validated' stays until the collapse
// reclassify leaves no spotlight in it.
const SPOTLIGHT_LIVE_STATUSES = ['experiment', 'validated', 'calibrating'];

// userRejectionReason stamped on the pending requests a brand's Pause all cancels. Written by the
// server, read wherever a rejected request is rendered or counted: it is the brand withdrawing,
// not the creator declining.
const PAUSE_ALL_REQUEST_REJECTION_REASON = 'brand_paused_spotlights';

// The resume-all refusal when nothing is paused and nothing is left to finish. The Manage budget
// modal tells it apart from a real failure by this exact text.
const PAUSE_ALL_NOTHING_TO_RESUME_MESSAGE = 'Spotlights are not paused for this brand.';

// Statuses whose set memberships a brand may still change: before launch the accepted picks are
// re-picked; live, blocked or paused, a set is added or held. Mid-launch (uploading, launching)
// the worker owns the memberships.
const SPOTLIGHT_GROUP_EDITABLE_STATUSES = ['readyToLaunch', 'paused', 'blocked', ...SPOTLIGHT_LIVE_STATUSES];

// Caps shared so the UI hard-stops exactly where the routes reject. One launch fans out into at
// most this many groups; a brand pins at most this many to its panel row.
const SPOTLIGHT_MAX_LAUNCH_GROUPS = 5;
// Cumulative spend below which a spotlight's grades stay uncolored; the server decides meetsDataGate,
// the row only explains it.
const SPOTLIGHT_DATA_GATE_MIN_SPEND = 50;
const SPOTLIGHT_MAX_PINNED_GROUPS = 4;

// ---------------------------------------------------------------------------
// Unsupported-platform gate (TEMPORARY)
//
// Mentions on these platforms can't be spotlighted yet — currently TikTok, whose
// mention videos aren't ripped/uploaded into our infrastructure, so there's no
// media to build an ad from. This single list drives every gate, client and
// server (card UI, request creation, accept, launch, and the worker backstop).
//
// To lift the gate: empty this array (`[]`) to neutralize every check at once,
// or remove the call sites entirely once all platforms are supported.
// ---------------------------------------------------------------------------
const UNSUPPORTED_SPOTLIGHT_PLATFORMS = ['tiktok'];

/**
 * Whether a mention on the given platform is eligible to be spotlighted.
 * @param {string|null|undefined} platform
 * @returns {boolean}
 */
const isMentionPlatformSpotlightable = platform => !UNSUPPORTED_SPOTLIGHT_PLATFORMS.includes(String(platform || '').toLowerCase());

// User-facing message shown wherever the gate blocks an action. Deliberately roadmap-free ("yet"
// would promise TikTok support to brands who can't act on it).
const UNSUPPORTED_SPOTLIGHT_PLATFORM_MESSAGE = "This content can't be spotlighted.";

// The actionable variant for viewers who can create the missing group themselves — the
// roadmap-free rule above protects brands who can't act, so a viewer who CAN act gets told how.
const needsGroupMessageFor = platform =>
  `${UNSUPPORTED_SPOTLIGHT_PLATFORM_MESSAGE} Create a ${getGroupPlatformSpec(platform).displayName} ${TERM.toLowerCase()} to enable it.`;

/**
 * The brand-capability form of the platform gate: a mention is spotlightable exactly when the
 * group platform its launch would land on is in the brand's launchable set (brandLaunchablePlatforms,
 * computed server-side and serialized onto brand payloads/mention decorations). Platform-unmapped
 * mentions launch on meta by construction, so they follow meta's membership.
 * Meta is the default membership: a caller without the serialized set (an undecorated surface)
 * keeps meta-lane mentions actionable while everything else stays gated.
 * A push lane the brand holds (pushEnabledPlatforms, the lane's target platforms) counts too: a
 * TikTok mention is spotlightable for a brand with only Meta sets once it can run there.
 * @param {{ platform?: string|null }|null|undefined} mention
 * @param {{ brandLaunchablePlatforms?: Set<string>|string[], pushEnabledPlatforms?: Set<string>|string[] }} [capabilities]
 * @returns {boolean}
 */
const isMentionSpotlightableForBrand = (mention, { brandLaunchablePlatforms = ['meta'], pushEnabledPlatforms = [] } = {}) => {
  const launchable = [...brandLaunchablePlatforms];
  const platforms = groupPlatformsForMentionPlatform(mention?.platform, { pushEnabledPlatforms });
  if (!platforms.length) platforms.push('meta');
  return platforms.some(platform => launchable.includes(platform));
};

// Meta URL macros ({{ad.name}}, {{placement}}) pass through Meta's url_tags verbatim, but TikTok
// never expands them — a UTM fragment carrying one would land literally in the landing page URL,
// so tiktok-group writes reject rather than store them.
const containsMetaUtmMacros = utm => /\{\{[^}]*\}\}/.test(String(utm || ''));

// A mention's platform decides which group platform can launch it — v1 routing is strict
// (instagram → meta, tiktok → tiktok). Unmapped platforms resolve to null; launch-routing callers
// fall back to meta, matching the server's request validation.
const MENTION_PLATFORM_TO_GROUP_PLATFORM = { instagram: 'meta', tiktok: 'tiktok' };

const groupPlatformForMentionPlatform = mentionPlatform => MENTION_PLATFORM_TO_GROUP_PLATFORM[String(mentionPlatform || '').toLowerCase()] || null;

// The push lanes' widening map: which group platforms a mention platform may ADDITIONALLY target
// with our hosted copy of its media, each behind that target platform's push flag. An Instagram
// mention pushes to TikTok as a dark post; a TikTok mention runs on Meta as a partnership ad of its
// video file. A future lane adds an entry, not a branch.
const PUSH_LANES = { instagram: ['tiktok'], tiktok: ['meta'] };

// The lane-aware form: every group platform a mention's launch may target. A push lane widens per
// PUSH_LANES only when its target platform is in pushEnabledPlatforms (the brand's push flags);
// everything else keeps the strict 1:1 map. This answers "which platforms may be targeted", not a
// fan-out — one launch still lands on exactly one platform.
const groupPlatformsForMentionPlatform = (mentionPlatform, { pushEnabledPlatforms = [] } = {}) => {
  const primary = groupPlatformForMentionPlatform(mentionPlatform);
  const platforms = primary ? [primary] : [];
  const enabled = [...pushEnabledPlatforms];
  for (const platform of PUSH_LANES[String(mentionPlatform || '').toLowerCase()] || []) {
    if (enabled.includes(platform)) platforms.push(platform);
  }
  return platforms;
};

// TikTok requires ad_text on a dark post of uploaded media (a Spark ad shows the creator's own post
// text), so every Push creative carries a caption. The /ad/create/ contract for the field: 1–100
// characters, no emoji, and no { or } (TikTok's caption spec reserves them).
const TIKTOK_AD_TEXT_MAX_LENGTH = 100;
// Extended_Pictographic also covers ©, ® and ™, which brand captions use as plain text — only their
// emoji-presentation form (followed by U+FE0F, stripped separately) counts.
const TIKTOK_AD_TEXT_EMOJI = /(?![©®™])\p{Extended_Pictographic}|\p{Emoji_Modifier}|\p{Regional_Indicator}|\u200D|\uFE0F|\u20E3/gu;

/**
 * @param {string|null|undefined} raw
 * @returns {{ valid: boolean, error: string|null }}
 */
const validateTikTokAdText = raw => {
  const value = String(raw === null || raw === undefined ? '' : raw).trim();

  if (!value) {
    return { valid: false, error: 'Add a caption for the TikTok ad.' };
  }
  if (value.length > TIKTOK_AD_TEXT_MAX_LENGTH) {
    return { valid: false, error: `Keep the TikTok caption to ${TIKTOK_AD_TEXT_MAX_LENGTH} characters.` };
  }
  if (value.replace(TIKTOK_AD_TEXT_EMOJI, '') !== value) {
    return { valid: false, error: "TikTok doesn't allow emoji in ad captions." };
  }
  if (/[{}]/.test(value)) {
    return { valid: false, error: "TikTok doesn't allow { or } in ad captions." };
  }

  return { valid: true, error: null };
};

// Cuts to at most maxLength UTF-16 units; a trailing high surrogate is half a pair, so it goes too.
const sliceWholeCodePoints = (text, maxLength) => {
  const sliced = text.slice(0, maxLength);
  return /[\uD800-\uDBFF]$/.test(sliced) ? sliced.slice(0, -1) : sliced;
};

// The caption a Push ad starts from when the brand writes none: the brand's name as one hashtag,
// so only letters, their marks and digits survive (#BallerinaFarm, #K18, #MEEM, #CaféRösch). A
// name with nothing to keep yields '' and validateTikTokAdText refuses it.
const TIKTOK_BRAND_HASHTAG_PREFIX = '#';
const defaultTikTokAdTextForBrand = brand => {
  // NFC first so a decomposed accent composes onto its letter; the emoji pass runs before marks
  // are kept because the variation selector and keycap are marks (and ℹ is a letter).
  const tag = String(brand?.name || '')
    .normalize('NFC')
    .replace(TIKTOK_AD_TEXT_EMOJI, '')
    .replace(/[^\p{L}\p{M}\p{N}]/gu, '');
  return tag ? sliceWholeCodePoints(`${TIKTOK_BRAND_HASHTAG_PREFIX}${tag}`, TIKTOK_AD_TEXT_MAX_LENGTH) : '';
};

// Attribution-window options per group platform, shared so the wizard's dropdown and the server's
// per-platform validation allowlists can't drift. Meta offers the eight {1d,7d} click × {off,1d}
// engage × {off,1d} view combos; TikTok v1.3 exposes only click/view windows — no engaged-view
// analogue — plus its own 14/28-day click windows. 7-day click is the recommended default on both.
const SPOTLIGHT_GROUP_ATTRIBUTION_OPTIONS = {
  meta: [
    { value: '7d_click', label: '7-day click', recommended: true },
    { value: '7d_click_1d_view', label: '7-day click + 1-day view' },
    { value: '7d_click_1d_engage', label: '7-day click + 1-day engage' },
    { value: '7d_click_1d_engage_1d_view', label: '7-day click + 1-day engage + 1-day view' },
    { value: '1d_click', label: '1-day click' },
    { value: '1d_click_1d_view', label: '1-day click + 1-day view' },
    { value: '1d_click_1d_engage', label: '1-day click + 1-day engage' },
    { value: '1d_click_1d_engage_1d_view', label: '1-day click + 1-day engage + 1-day view' }
  ],
  tiktok: [
    { value: '7d_click', label: '7-day click', recommended: true },
    { value: '7d_click_1d_view', label: '7-day click + 1-day view' },
    { value: '1d_click', label: '1-day click' },
    { value: '1d_click_1d_view', label: '1-day click + 1-day view' },
    { value: '14d_click', label: '14-day click' },
    { value: '28d_click', label: '28-day click' }
  ]
};

/**
 * @param {string|null|undefined} platform
 * @returns {Array<{ value: string, label: string, recommended?: boolean }>}
 */
const getSpotlightGroupAttributionOptions = platform =>
  SPOTLIGHT_GROUP_ATTRIBUTION_OPTIONS[String(platform || 'meta').toLowerCase()] || SPOTLIGHT_GROUP_ATTRIBUTION_OPTIONS.meta;

// Per-platform group facts shared by the client (wizard, settings surfaces) and the server's group
// validation — the pure half; server-only behavior bits (pilot flags, advertiser topology, legacy
// columns) live in node's spotlight_group_helpers spec. Platform #3 adds an entry here plus one
// there.
//   allowedObjectives    — objectives a group of this platform may hold (admin gating is
//                          platform-independent: brands pick sales only)
//   conversionEvents     — fixed conversion-event allowlist, or null when the platform takes
//                          free-form events
//   pixel                — id format + whether a group can exist without one, with the invalid-id copy
//   allowsUtmMacros      — whether {{...}} URL macros expand on the platform (Meta-only concept)
//   minDailyBudgetCents  — per-group daily floor, or null when the account minimum governs
//                          (TikTok budgets must strictly exceed $20, so the floor is $20.01)
const GROUP_PLATFORM_SPECS = {
  meta: {
    displayName: 'Meta',
    allowedObjectives: ['sales', 'traffic', 'app_promotion'],
    conversionEvents: null,
    pixel: {
      pattern: /^\d{5,20}$/,
      required: false,
      invalidCopy: 'That does not look like a Meta pixel ID — use the numeric ID from Meta Events Manager.'
    },
    allowsUtmMacros: true,
    minDailyBudgetCents: null,
    needsGroupMessage: false,
    // A custom conversion is a Meta ad-account object; the reads and writes behind it speak the
    // Graph API, so a platform without them must never reach that code with its own account id.
    supportsCustomConversions: true
  },
  tiktok: {
    displayName: 'TikTok',
    allowedObjectives: ['sales'],
    conversionEvents: ['SHOPPING'],
    pixel: {
      pattern: /^[A-Za-z0-9]{1,64}$/,
      required: true,
      invalidCopy: 'That does not look like a TikTok pixel ID — use the ID from TikTok Events Manager.'
    },
    allowsUtmMacros: false,
    minDailyBudgetCents: 2001,
    needsGroupMessage: true,
    supportsCustomConversions: false
  }
};

const getGroupPlatformSpec = platform => GROUP_PLATFORM_SPECS[String(platform || 'meta').toLowerCase()] || GROUP_PLATFORM_SPECS.meta;

// Derived export for the budget surfaces; the spec above is the constant's one home (the server's
// ad-platform adapter derives its floor from it too).
const TIKTOK_MIN_GROUP_DAILY_BUDGET_CENTS = GROUP_PLATFORM_SPECS.tiktok.minDailyBudgetCents;

// The BrandSettings.featureFlags keys that stage Spotlights rollouts, set per brand from the admin
// Spotlights dashboard. `requires` names the flags a key does nothing without; `recordsEnabledAt` marks a
// flag whose behavior keys off the moment it turned on, so the admin write stamps that moment beside it and
// refuses to turn it back off.
const SPOTLIGHT_BRAND_FEATURE_FLAGS = [
  {
    key: 'useSpotlightGroupsTikTok',
    label: `TikTok ${TERM_PLURAL.toLowerCase()}`,
    description: `TikTok ${TERM_PLURAL.toLowerCase()}: the TikTok wizard card, TikTok launch targeting, and the TikTok Data Sets pane.`,
    requires: []
  },
  {
    key: 'useSpotlightGroupsLinkClicksGoal',
    label: 'Link-clicks goal',
    description: `The "Maximize link clicks" performance goal on Meta sales ${TERM_PLURAL.toLowerCase()}, for traffic to retailers whose purchases the brand's pixel can't see.`,
    requires: []
  },
  {
    key: 'useNorthbeamPlatformLabels',
    label: 'Northbeam platform labels',
    description: `Adds the ad platform to the Northbeam campaign label and spend feed ("- TikTok", "- Meta"). TikTok ${TERM_PLURAL.toLowerCase()} switch the day after it turns on, which sends their ads back through TikTok review; Meta ${TERM_PLURAL.toLowerCase()} switch only when created after it, so live Meta ads are untouched. It can't be turned off, since spend already sent under the new labels would count twice.`,
    requires: [],
    recordsEnabledAt: true
  }
];

const SPOTLIGHT_BRAND_FEATURE_FLAG_KEYS = SPOTLIGHT_BRAND_FEATURE_FLAGS.map(flag => flag.key);

/**
 * Every flag `key` needs, directly or through another prerequisite, in list order.
 * @param {string} key
 * @returns {string[]}
 */
const getSpotlightBrandFeatureFlagPrerequisites = key => {
  const needed = new Set();
  const visit = k =>
    (SPOTLIGHT_BRAND_FEATURE_FLAGS.find(flag => flag.key === k)?.requires || []).forEach(req => {
      if (needed.has(req)) return;
      needed.add(req);
      visit(req);
    });
  visit(key);
  return SPOTLIGHT_BRAND_FEATURE_FLAG_KEYS.filter(k => needed.has(k));
};

/**
 * Every flag that stops working without `key`, in list order.
 * @param {string} key
 * @returns {string[]}
 */
const getSpotlightBrandFeatureFlagDependents = key =>
  SPOTLIGHT_BRAND_FEATURE_FLAG_KEYS.filter(k => getSpotlightBrandFeatureFlagPrerequisites(k).includes(key));

/**
 * The Strategies rollout flags out of a brand's featureFlags, each true, false, or null when unset.
 * @param {object|string|null|undefined} featureFlags - the column as stored or as parsed
 * @returns {Object<string, boolean|null>}
 */
const pickSpotlightBrandFeatureFlags = featureFlags => {
  const flags = parseSpotlightBrandFeatureFlags(featureFlags);
  return Object.fromEntries(SPOTLIGHT_BRAND_FEATURE_FLAG_KEYS.map(key => [key, flags && flags[key] != null ? !!flags[key] : null]));
};

const spotlightBrandFeatureFlagEnabledAtKey = key => `${key}EnabledAt`;

/**
 * When a `recordsEnabledAt` flag turned on for a brand, or null while it's off or carries no stamp.
 * @param {object|string|null|undefined} featureFlags - the column as stored or as parsed
 * @param {string} key
 * @returns {Date|null}
 */
const getSpotlightBrandFeatureFlagEnabledAt = (featureFlags, key) => {
  const flags = parseSpotlightBrandFeatureFlags(featureFlags);
  const stamp = flags?.[key] === true ? flags[spotlightBrandFeatureFlagEnabledAtKey(key)] : null;
  const enabledAt = typeof stamp === 'string' ? new Date(stamp) : null;
  return enabledAt && !Number.isNaN(enabledAt.getTime()) ? enabledAt : null;
};

/**
 * `featureFlags` with every Spotlights rollout flag, and its enable stamp, taken from `currentFeatureFlags`,
 * so a writer saving the whole column from an older copy can't drop or revive one.
 * @param {object} featureFlags - the parsed column a writer is about to save
 * @param {object|string|null|undefined} currentFeatureFlags - the column as stored now
 * @returns {object}
 */
const withCurrentSpotlightBrandFeatureFlags = (featureFlags, currentFeatureFlags) => {
  const current = parseSpotlightBrandFeatureFlags(currentFeatureFlags) || {};
  const ownedKeys = SPOTLIGHT_BRAND_FEATURE_FLAGS.flatMap(flag =>
    flag.recordsEnabledAt ? [flag.key, spotlightBrandFeatureFlagEnabledAtKey(flag.key)] : [flag.key]
  );
  const kept = Object.fromEntries(Object.entries(featureFlags).filter(([key]) => !ownedKeys.includes(key)));
  ownedKeys.filter(key => key in current).forEach(key => (kept[key] = current[key]));
  return kept;
};

const parseSpotlightBrandFeatureFlags = featureFlags => {
  if (typeof featureFlags !== 'string') return featureFlags || null;
  try {
    return JSON.parse(featureFlags);
  } catch {
    return null;
  }
};

// ---------------------------------------------------------------------------
// Creator-level kill switch (admin lever)
//
// When an admin disables Spotlights for a creator (UserSettings.spotlightsDisabledAt),
// brands can't request them, nothing launches, and live spotlights pause. Wherever we
// surface that state — the disabled mention CTA tooltip, the creator's Spotlights page,
// the brand's paused card — we always show this generic line and OPTIONALLY append the
// admin-entered reason if one was supplied. Keeping the copy + composition here keeps it
// identical on client and server.
// ---------------------------------------------------------------------------
const SPOTLIGHTS_DISABLED_GENERIC_MESSAGE = 'This creator is currently unavailable for Spotlights.';

/**
 * Build the message shown when a creator's Spotlights are disabled: always the generic
 * line, with the admin reason appended when present.
 * @param {string|null|undefined} adminReason
 * @returns {string}
 */
const buildSpotlightsDisabledMessage = adminReason => {
  const note = String(adminReason || '').trim();
  return note ? `${SPOTLIGHTS_DISABLED_GENERIC_MESSAGE} ${note}` : SPOTLIGHTS_DISABLED_GENERIC_MESSAGE;
};

// ---------------------------------------------------------------------------
// Per-spotlight admin triage ("Error section" lever)
//
// The per-spotlight sibling of the creator kill switch above. When an admin marks a single
// spotlight errored (Spotlights.erroredAt), it pauses and leaves the pipeline, and the
// creator/brand see a NEUTRAL "under review" state — never the admin's reason. Copy lives here
// next to the kill-switch copy, in the same shape, so client and server stay identical. Unlike
// the kill switch, triage is a transient "we're looking into it" review, not an eligibility
// cutoff, so it deliberately does NOT direct the creator to contact support.
// ---------------------------------------------------------------------------
const SPOTLIGHT_UNDER_REVIEW_GENERIC_MESSAGE = 'This Spotlight is currently under review.';
const SPOTLIGHT_NEEDS_ATTENTION_GENERIC_MESSAGE = 'This Spotlight needs attention and is paused while we review it.';

// Brand-facing tooltips for the two admin holds, shown on the (blocked-style) spotlight row in the
// brand table. Neutral — no admin reason or account specifics. Triage = a delivery error we're
// investigating; kill switch = an advertising-partner restriction.
const SPOTLIGHT_BRAND_ERRORED_TOOLTIP =
  'This Spotlight is paused due to a delivery error and is being investigated. No action is needed from you right now.';
const SPOTLIGHT_BRAND_RESTRICTED_TOOLTIP = 'This Spotlight is paused due to a restriction from one of our advertising partners.';

/**
 * Build the creator-facing message for a spotlight in admin triage. Generic only — the admin
 * reason is never surfaced. Kept in the same shape as buildSpotlightsDisabledMessage.
 * @returns {string}
 */
const buildSpotlightUnderReviewMessage = () => SPOTLIGHT_UNDER_REVIEW_GENERIC_MESSAGE;

// Seed reasons for the admin triage queue ("Error section"). The admin picks a `key` (stored on
// Spotlights.errorType) plus a required free-text note. Seeded from the real recurring hard errors;
// all reasons behave identically (per-spotlight pause + generic creator/brand copy). 'other' is the
// catch-all. Shared here so the admin UI dropdown (client) and the route validation (server) agree.
const SPOTLIGHT_ERROR_REASONS = [
  { key: 'stuck_in_launching', label: 'Stuck in launching', description: 'Authorization active, but stuck in launching — ad was never created on Meta.' },
  { key: 'not_monetized', label: 'Not monetized', description: 'Account flagged not monetizable.' },
  { key: 'other', label: 'Other', description: 'Anything else — explain in the note.' }
];
const SPOTLIGHT_ERROR_REASON_KEYS = SPOTLIGHT_ERROR_REASONS.map(r => r.key);

// Actionable fix steps for a spotlight blocked at publish because the creator hasn't granted the
// advertise-level partnership-ad permission (Meta #200 / subcode 4289001), or no creator Facebook
// Page resolved. ShopMy can't flip this — only the creator can, in Meta Business Suite. Surfaced in
// the #server-spotlights alert so ops can act / forward to the creator. Shared so any future
// creator-facing surface can reuse the same copy.
const SPOTLIGHT_PARTNERSHIP_FIX_STEPS =
  '*To fix (creator action required):* the creator must grant ShopMy *partnership ad* permission in ' +
  'Meta Business Suite — this is separate from tagging ShopMy as a paid partner.\n' +
  '1. Instagram → Settings → *Creator tools and controls* → *Branded content* (or Meta Business Suite → ' +
  'Settings → *Branded content*).\n' +
  '2. Under *Approved business partners*, find *ShopMy* and turn on *“Allow business partner to promote”* / ' +
  '*Ads* (account-level), not just the branded-content tag.\n' +
  '3. Once enabled, reply here and we’ll re-launch.';

// Fix steps for a TikTok launch blocked on the creator's side: ShopMy's Business Center authorization
// is missing, revoked or expired, the identity can't promote or push video, or the post's sound isn't
// licensed for ads. Only the creator can grant it, from the TikTok connection step in their ShopMy
// Spotlights settings — TikTok has no Business Suite permission to flip.
const SPOTLIGHT_TIKTOK_PARTNERSHIP_FIX_STEPS =
  '*To fix (creator action required):* the creator must authorize ShopMy for TikTok ads from the *TikTok connection step* ' +
  'in their ShopMy Spotlights settings (it opens TikTok’s Business Center authorization).\n' +
  '1. A revoked or expired authorization is re-granted the same way — ask them to go through the step again.\n' +
  '2. A pushed post also needs their push consent on file — refreshing the TikTok connection in their Spotlights settings records it.\n' +
  '3. Once authorized, relaunch from the admin sync modal.';

// Fix steps for a TikTok launch blocked by the post itself: a photo post or one whose id can't be
// read, a deleted or private video, a mention with no hosted video to push, or a sound not licensed
// for ads in the strategy's regions. The authorization is fine; only another post can launch.
const SPOTLIGHT_TIKTOK_CONTENT_FIX_STEPS =
  '*To fix (creator action required):* this post can’t be promoted on TikTok — the reason names why (photo post, deleted or ' +
  'private video, no hosted video, or a sound not licensed for ads in the targeted regions). Ask the creator for another post ' +
  '(or an original sound), then launch that one.';

// Fix steps for a launch TikTok itself refused over the advertiser or its ad groups: ad-group quota,
// a deleted ad group, an advertiser hold (balance, contract, review) or a pixel the advertiser can't
// use. Nothing in Spotlight Settings fixes an unfunded advertiser — the remedy is in TikTok Ads
// Manager, and the creator has nothing to grant.
const SPOTLIGHT_TIKTOK_ADVERTISER_FIX_STEPS =
  '*To fix (ShopMy admin action required):* TikTok refused the advertiser or ad group — check the advertiser’s balance, contract ' +
  'and review status and the strategy’s ad groups in TikTok Ads Manager (a deleted ad group is retired from the pool on the next ' +
  'launch; a full advertiser needs a new one linked to the strategy), then relaunch from the admin sync modal.';

// Fix steps for a spotlight blocked at publish because the stored audience settings (a group's, or
// the brand's legacy bucket) name a custom audience or interest Meta can no longer resolve
// (subcodes 1359207 / 1487079), or an attribution window its performance goal doesn't allow. Most
// 1359207s are shares the brand sent that nobody accepted yet, so the accept path comes first.
// Relaunching rebuilds the identical spec, so the share has to be accepted or the settings changed first.
const SPOTLIGHT_AD_SET_CONFIG_FIX_STEPS =
  '*To fix (admin action required):* accept the pending audience share in Ads Manager (Audiences → shared with you), ' +
  'or open the brand’s Spotlight audience settings (the group’s, if it launches into a group) and remove the custom ' +
  'audience or interest Meta can no longer resolve — then relaunch the spotlight.';

// SPOTLIGHT_AD_SET_CONFIG_FIX_STEPS narrowed to subcode 1359207, raised only after the launch already accepted any
// share pending on ShopMy's business.
const SPOTLIGHT_UNAVAILABLE_AUDIENCE_FIX_STEPS =
  '*To fix (admin action required):* ask the brand to share the audience with ShopMy’s Spotlights ad account in Meta Ads Manager ' +
  '(Audiences → Share), then accept it under Audiences → shared with you if it shows as pending, and relaunch the spotlight.';

// Fix steps for a launch our own pre-launch gates refused over the strategy's setup — a TikTok
// strategy with no advertiser or pixel, an unsupported objective, a held or disabled strategy, the
// per-group stats writer still off, or an advertiser out of ad-group quota. ShopMy config or
// capacity every time: the creator has nothing to grant, so the partnership steps must not post.
const SPOTLIGHT_GROUP_CONFIG_FIX_STEPS =
  '*To fix (ShopMy admin action required):* the reason names the strategy setting or environment flag that is missing — ' +
  'set it (Spotlight Settings for the strategy, the deploy environment for a flag), then relaunch from the admin sync modal.';

// Fix steps for a spotlight blocked at publish because Meta could not pair the ad's two identities
// (subcode 3867111). The launch already retries a cached Page as an Instagram-only identity, so an
// alert means the Page came straight from Meta's edge or the retry was refused too. A ShopMy config
// problem either way — the creator has nothing to grant, so the partnership-permission steps must not
// be forwarded to them.
const SPOTLIGHT_IDENTITY_PAIRING_FIX_STEPS =
  '*To fix (engineering — no creator action needed):* Meta couldn’t pair the ad’s identities from the creator’s linked ' +
  'Facebook Page, and the Instagram-only retry didn’t clear it. Don’t send the creator the partnership-permission steps; ' +
  'check the Page ↔ Instagram link on the creator’s Meta grant, then relaunch from the admin sync modal.';

// Whether the creator-paused pop-up should still surface this spotlight. Timestamp comparison, not
// a null check: a re-pause after a dismissal moves pausedAt past the stamp and re-fires. A blocked
// row with no pausedAt (paused before the column existed) shows until dismissed once, then stays
// dismissed.
const isPausedModalPending = spotlight => {
  if (!spotlight || spotlight.status !== 'blocked') return false;
  if (!spotlight.pausedModalDismissedAt) return true;
  if (!spotlight.pausedAt) return false;
  return new Date(spotlight.pausedAt).getTime() > new Date(spotlight.pausedModalDismissedAt).getTime();
};

// Companion for the client's optimistic layer: given the pausedAt captured when this spotlight was
// dismissed, decide whether the row's current pause is still that dismissed one. A pause that
// advanced past the capture is new and shows again; a legacy row (no pausedAt) is covered by any
// dismissal.
const isPauseCoveredByDismissal = (spotlight, dismissedPausedAt) => {
  if (!spotlight?.pausedAt) return true;
  if (!dismissedPausedAt) return false;
  return new Date(spotlight.pausedAt).getTime() <= new Date(dismissedPausedAt).getTime();
};

// The app stores a Spotlight app-promotion set can point at. Meta requires the ad set's OS targeting
// to match the promoted app's store, so the store URL is what decides the OS — shared with the
// client so the wizard rejects an unusable link before the write does.
const APP_STORE_OS = { apple: 'iOS', google: 'Android' };

const appStoreOs = objectStoreUrl => {
  let host;
  try {
    host = new URL(String(objectStoreUrl)).hostname.toLowerCase();
  } catch {
    return null;
  }
  if (host === 'apple.com' || host.endsWith('.apple.com')) return APP_STORE_OS.apple;
  if (host === 'play.google.com') return APP_STORE_OS.google;
  return null;
};

const isAppStoreUrl = objectStoreUrl => !!appStoreOs(objectStoreUrl);

// An app-promotion set's app lives in the SpotlightGroups.conversionConfig JSON, the column the
// groups table reserves for promoted-object fields with no typed column. Shared so the Meta key
// names have one definition rather than one per side. Accepts the raw column (string or parsed).
const readAppPromotionConfig = conversionConfig => {
  let parsed = conversionConfig;
  if (typeof parsed === 'string') {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      parsed = null;
    }
  }
  const trimmed = value => (value == null ? null : String(value).trim() || null);
  return {
    applicationId: trimmed(parsed?.application_id),
    objectStoreUrl: trimmed(parsed?.object_store_url)
  };
};

// The in-app events an app-promotion set may optimize for. Shared so the wizard's dropdown and the
// write-time whitelist can't drift apart — every value uppercases to a documented Meta
// custom_event_type. COMPLETE_REGISTRATION is the one verified against a live app ad set.
// Deliberately absent: an install is Meta's APP_INSTALLS optimization goal, not a custom event, so
// there is no INSTALL token to send; and INITIATE_CHECKOUT is spelled INITIATED_CHECKOUT in Meta's
// enum, so it stays off this list until the sales list's spelling is fixed too.
const SPOTLIGHT_APP_CONVERSION_EVENTS = [
  { value: 'complete_registration', label: 'Complete Registration', recommended: true },
  { value: 'purchase', label: 'Purchase' },
  { value: 'add_to_cart', label: 'Add to Cart' },
  { value: 'add_payment_info', label: 'Add Payment Info' }
];

const isAppConversionEvent = value => SPOTLIGHT_APP_CONVERSION_EVENTS.some(option => option.value === value);

// Whether a set's conversion event carries money. Only a purchase does: Meta's action_values and
// purchase_roas are both purchase-scoped, so every other event's revenue is undefined rather than
// zero. Shared because two very different decisions turn on it — the UI shows a dash instead of a
// 0.00 that reads as failure, and the auto-pause verdict must not grade a set on revenue it can
// never earn. No configured event means purchase, as it does everywhere else.
// Purchase-equivalent optimization events: Meta's PURCHASE and TikTok's SHOPPING (Complete Payment,
// whose complete_payment metrics the TikTok adapter already maps to purchases and revenue). The
// wizard stores the value lower-cased and the platform specs list it upper-cased.
const REVENUE_CONVERSION_EVENTS = ['purchase', 'shopping'];
const isRevenueConversionEvent = conversionEvent => !conversionEvent || REVENUE_CONVERSION_EVENTS.includes(String(conversionEvent).toLowerCase());

const buildAppPromotionConfig = ({ applicationId, objectStoreUrl }) => ({
  application_id: String(applicationId),
  object_store_url: String(objectStoreUrl)
});

module.exports = {
  TERM,
  TERM_PLURAL,
  UTM_PARAMS_MAX_LENGTH,
  RESERVED_UTM_PARAM_KEYS,
  validateUtmParams,
  normalizeUtmParams,
  appendUtmParams,
  UTM_PRESETS_MAX,
  UTM_PRESET_NAME_MAX_LENGTH,
  validateUtmPresets,
  PAUSE_REASON_MIN_LENGTH,
  PAUSE_REASON_MAX_LENGTH,
  validatePauseReason,
  SPOTLIGHT_MIN_DAILY_BUDGET,
  validateDailyBudget,
  SPOTLIGHT_TARGET_ROAS_FLOOR,
  SPOTLIGHT_TARGET_ROAS_CEILING,
  SPOTLIGHT_DEFAULT_TARGET_ROAS,
  SPOTLIGHT_DEFAULT_PAUSE_GUIDE_ROAS,
  SPOTLIGHT_PAUSE_GUIDE_FLOOR,
  resolveTargetRoasBand,
  resolveSpotlightTargetBand,
  validateTargetRoas,
  validatePauseGuideRoas,
  SPOTLIGHT_META_PLACEMENTS,
  SPOTLIGHT_META_PLACEMENT_VALUES,
  allowlistedMetaPlacements,
  includesEveryMetaPlacement,
  normalizedMetaPlacements,
  isBrandVisibleGroup,
  SPOTLIGHT_LIVE_STATUSES,
  PAUSE_ALL_REQUEST_REJECTION_REASON,
  PAUSE_ALL_NOTHING_TO_RESUME_MESSAGE,
  SPOTLIGHT_GROUP_EDITABLE_STATUSES,
  SPOTLIGHT_MAX_LAUNCH_GROUPS,
  SPOTLIGHT_DATA_GATE_MIN_SPEND,
  SPOTLIGHT_MAX_PINNED_GROUPS,
  UNSUPPORTED_SPOTLIGHT_PLATFORMS,
  isMentionPlatformSpotlightable,
  isMentionSpotlightableForBrand,
  containsMetaUtmMacros,
  MENTION_PLATFORM_TO_GROUP_PLATFORM,
  PUSH_LANES,
  groupPlatformForMentionPlatform,
  groupPlatformsForMentionPlatform,
  TIKTOK_AD_TEXT_MAX_LENGTH,
  validateTikTokAdText,
  defaultTikTokAdTextForBrand,
  getSpotlightGroupAttributionOptions,
  GROUP_PLATFORM_SPECS,
  getGroupPlatformSpec,
  TIKTOK_MIN_GROUP_DAILY_BUDGET_CENTS,
  SPOTLIGHT_BRAND_FEATURE_FLAGS,
  SPOTLIGHT_BRAND_FEATURE_FLAG_KEYS,
  getSpotlightBrandFeatureFlagPrerequisites,
  getSpotlightBrandFeatureFlagDependents,
  pickSpotlightBrandFeatureFlags,
  spotlightBrandFeatureFlagEnabledAtKey,
  getSpotlightBrandFeatureFlagEnabledAt,
  withCurrentSpotlightBrandFeatureFlags,
  UNSUPPORTED_SPOTLIGHT_PLATFORM_MESSAGE,
  needsGroupMessageFor,
  SPOTLIGHTS_DISABLED_GENERIC_MESSAGE,
  buildSpotlightsDisabledMessage,
  SPOTLIGHT_UNDER_REVIEW_GENERIC_MESSAGE,
  SPOTLIGHT_NEEDS_ATTENTION_GENERIC_MESSAGE,
  SPOTLIGHT_BRAND_ERRORED_TOOLTIP,
  SPOTLIGHT_BRAND_RESTRICTED_TOOLTIP,
  buildSpotlightUnderReviewMessage,
  SPOTLIGHT_ERROR_REASONS,
  SPOTLIGHT_ERROR_REASON_KEYS,
  SPOTLIGHT_PARTNERSHIP_FIX_STEPS,
  SPOTLIGHT_TIKTOK_PARTNERSHIP_FIX_STEPS,
  SPOTLIGHT_TIKTOK_CONTENT_FIX_STEPS,
  SPOTLIGHT_TIKTOK_ADVERTISER_FIX_STEPS,
  SPOTLIGHT_AD_SET_CONFIG_FIX_STEPS,
  SPOTLIGHT_UNAVAILABLE_AUDIENCE_FIX_STEPS,
  SPOTLIGHT_GROUP_CONFIG_FIX_STEPS,
  SPOTLIGHT_IDENTITY_PAIRING_FIX_STEPS,
  isPausedModalPending,
  isPauseCoveredByDismissal,
  APP_STORE_OS,
  appStoreOs,
  isAppStoreUrl,
  readAppPromotionConfig,
  buildAppPromotionConfig,
  SPOTLIGHT_APP_CONVERSION_EVENTS,
  isAppConversionEvent,
  isRevenueConversionEvent
};
